import { useApplication } from '@/hooks/useApplication';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useThemeMode } from '@/hooks/useThemeMode';
import { OpenEditType, Subroutine } from '@/types/subroutine';
import { findSubroutine } from '@/util/application';
import { getFolderLabels } from '@/util/folder';
import { ArrowBack } from '@mui/icons-material';
import { AppBar, Box, Button, Stack, Toolbar, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import EditMenu from './menu/EditMenu';
import FileMenu from './menu/FileMenu';
import NavBarActions from './NavBarActions';
import NavigationButtons from './NavigationButtons';

type BlockAppBarProps = {
  closeDetails?: () => void;
  openEdit?: (openEdit: OpenEditType) => void;
  disableButtons?: boolean;
};

const BlockAppBar: FC<BlockAppBarProps> = (props) => {
  const { mode } = useThemeMode();
  const { application } = useApplication();
  const { subroutineHistory, selectedSubroutineId, setSelectedSubroutineId } = useSubroutine();
  const subroutine =
    application && selectedSubroutineId && findSubroutine(application.subroutines, selectedSubroutineId);

  const handleHistory = () => {
    const newSubroutineId = subroutineHistory[subroutineHistory.length - 1];
    setSelectedSubroutineId(newSubroutineId, true);
  };

  const getSubroutineLabel = (subroutine: Subroutine) => {
    const result = subroutine.folder && application ? getFolderLabels(subroutine.folder, application) + '.' : '';
    return result + subroutine.label;
  };

  const theme = useTheme();
  const appBarColor = theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[200];

  return (
    <AppBar
      position='relative'
      elevation={0}
      sx={{
        bgcolor: appBarColor,
        color: theme.palette.getContrastText(appBarColor)
      }}
    >
      <Toolbar
        variant='dense'
        sx={{
          gap: 0,
          flexDirection: 'column'
        }}
        disableGutters
      >
        <Stack direction='row' height='100%' width='100%' alignItems='center' pl={1} pr={2}>
          {!props.disableButtons ? (
            <Box display={'flex'}>
              <FileMenu closeDetails={() => props.closeDetails && props.closeDetails()} />
              <EditMenu openEdit={(openEdit) => props.openEdit && props.openEdit(openEdit)} />
            </Box>
          ) : (
            <Box display={'flex'}>
              <Button disabled color='inherit'>
                File
              </Button>
              <Button disabled color='inherit'>
                Edit
              </Button>
            </Box>
          )}
          <NavigationButtons />
          <Typography
            variant='h6'
            color='inherit'
            component='div'
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {subroutineHistory.length > 0 && (
              <Box display={'flex'} mr={1} sx={{ cursor: 'pointer' }} onClick={handleHistory}>
                <ArrowBack />
              </Box>
            )}
            {subroutine && `${getSubroutineLabel(subroutine)} - `}
            {selectedSubroutineId && `${application.label} (${application.name || 'untitled'}) - `}
            Block Engine Builder
          </Typography>
          {/* <TestToolbar /> */}
          <NavBarActions />
        </Stack>
        <Box
          sx={{
            height: mode === 'light' ? theme.spacing(0.5) : theme.spacing(0.25),
            width: '100%',
            bgcolor: 'primary.main'
          }}
        />
      </Toolbar>
    </AppBar>
  );
};

export default BlockAppBar;
